import { CourtRoom, LocalCache, PendingWrite, PendingWriteType, SyncResult } from '../types';

const CACHE_KEY = 'court-splitter-cache';
const MAX_RETRY_COUNT = 3;

/**
 * CourtSplitterCache — Manages local storage cache and offline write queue.
 * Provides optimistic updates and background sync capabilities.
 */
export class CourtSplitterCache {
  private cache: LocalCache;

  constructor() {
    this.cache = this.loadCache();
  }

  // ============================================================================
  // Cache Persistence
  // ============================================================================

  private loadCache(): LocalCache {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return this.emptyCache();
      const parsed = JSON.parse(raw) as LocalCache;
      // Validate structure
      if (!Array.isArray(parsed.rooms) || !Array.isArray(parsed.pendingWrites)) {
        return this.emptyCache();
      }
      return parsed;
    } catch {
      return this.emptyCache();
    }
  }

  private emptyCache(): LocalCache {
    return {
      rooms: [],
      pendingWrites: [],
      lastSyncTimestamp: 0,
    };
  }

  private saveCache(): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.cache));
    } catch (e) {
      console.warn('[CourtSplitterCache] Failed to save cache:', e);
    }
  }

  // ============================================================================
  // Room Cache Operations
  // ============================================================================

  /** Get all cached rooms */
  getRooms(): CourtRoom[] {
    return [...this.cache.rooms];
  }

  /** Get a specific room by code */
  getRoom(roomCode: string): CourtRoom | null {
    return this.cache.rooms.find(r => r.id === roomCode) || null;
  }

  /** Get rooms for a specific user (as member) */
  getUserRooms(userId: string): CourtRoom[] {
    return this.cache.rooms.filter(r =>
      r.members.some(m => m.id === userId)
    );
  }

  /** Update or insert a room in the cache */
  upsertRoom(room: CourtRoom): void {
    const index = this.cache.rooms.findIndex(r => r.id === room.id);
    if (index >= 0) {
      this.cache.rooms[index] = room;
    } else {
      this.cache.rooms.push(room);
    }
    this.saveCache();
  }

  /** Remove a room from the cache */
  removeRoom(roomCode: string): void {
    this.cache.rooms = this.cache.rooms.filter(r => r.id !== roomCode);
    this.saveCache();
  }

  /** Replace all cached rooms (after full sync) */
  setRooms(rooms: CourtRoom[]): void {
    this.cache.rooms = rooms;
    this.cache.lastSyncTimestamp = Date.now();
    this.saveCache();
  }

  /** Get last sync timestamp */
  getLastSyncTimestamp(): number {
    return this.cache.lastSyncTimestamp;
  }

  /** Update last sync timestamp */
  updateSyncTimestamp(): void {
    this.cache.lastSyncTimestamp = Date.now();
    this.saveCache();
  }

  // ============================================================================
  // Pending Write Queue
  // ============================================================================

  /** Add a write operation to the offline queue */
  addPendingWrite(type: PendingWriteType, roomCode: string, payload: any): string {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const write: PendingWrite = {
      id,
      type,
      roomCode,
      payload,
      timestamp: Date.now(),
      retryCount: 0,
    };
    this.cache.pendingWrites.push(write);
    this.saveCache();
    return id;
  }

  /** Get all pending writes (ordered by timestamp) */
  getPendingWrites(): PendingWrite[] {
    return [...this.cache.pendingWrites].sort((a, b) => a.timestamp - b.timestamp);
  }

  /** Get pending write count */
  getPendingWriteCount(): number {
    return this.cache.pendingWrites.length;
  }

  /** Remove a pending write (after successful sync) */
  removePendingWrite(writeId: string): void {
    this.cache.pendingWrites = this.cache.pendingWrites.filter(w => w.id !== writeId);
    this.saveCache();
  }

  /** Increment retry count for a failed write */
  incrementRetryCount(writeId: string): void {
    const write = this.cache.pendingWrites.find(w => w.id === writeId);
    if (write) {
      write.retryCount++;
      this.saveCache();
    }
  }

  /** Remove writes that have exceeded max retries */
  pruneFailedWrites(): PendingWrite[] {
    const failed = this.cache.pendingWrites.filter(w => w.retryCount >= MAX_RETRY_COUNT);
    this.cache.pendingWrites = this.cache.pendingWrites.filter(w => w.retryCount < MAX_RETRY_COUNT);
    this.saveCache();
    return failed;
  }

  /** Clear all pending writes */
  clearPendingWrites(): void {
    this.cache.pendingWrites = [];
    this.saveCache();
  }

  /** Check if there are pending writes for a specific room */
  hasPendingWritesForRoom(roomCode: string): boolean {
    return this.cache.pendingWrites.some(w => w.roomCode === roomCode);
  }

  // ============================================================================
  // Optimistic Updates
  // ============================================================================

  /**
   * Apply an optimistic update to the local cache.
   * This updates the cached room data immediately before the API confirms.
   */
  applyOptimisticUpdate(roomCode: string, updater: (room: CourtRoom) => CourtRoom): void {
    const room = this.getRoom(roomCode);
    if (!room) return;

    const updated = updater(room);
    updated.updatedAt = Date.now();
    this.upsertRoom(updated);
  }

  /**
   * Rollback an optimistic update by replacing with server data.
   */
  rollbackOptimisticUpdate(roomCode: string, serverRoom: CourtRoom): void {
    this.upsertRoom(serverRoom);
  }

  // ============================================================================
  // Migration from legacy storage
  // ============================================================================

  /**
   * Migrate data from the old CourtSplitterEngine localStorage format.
   * The old format stored rooms under 'court-splitter-rooms' key.
   */
  migrateFromLegacy(): CourtRoom[] {
    try {
      const raw = localStorage.getItem('court-splitter-rooms');
      if (!raw) return [];

      const legacyRooms = JSON.parse(raw) as any[];
      const migrated: CourtRoom[] = legacyRooms.map(r => ({
        ...r,
        updatedAt: r.createdAt || Date.now(),
        currency: 'USD' as const,
        payments: [],
        members: (r.members || []).map((m: any) => ({
          ...m,
          isAdmin: m.id === r.createdBy,
          isLinked: !m.id.startsWith('demo-') && !m.id.includes('-'),
        })),
        sessions: (r.sessions || []).map((s: any) => ({
          ...s,
          createdBy: s.paidBy || r.createdBy,
          createdAt: new Date(s.date).getTime(),
        })),
      }));

      // Store in new cache format
      for (const room of migrated) {
        this.upsertRoom(room);
      }

      // Remove legacy key
      localStorage.removeItem('court-splitter-rooms');

      return migrated;
    } catch {
      return [];
    }
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  /** Clear entire cache */
  clearAll(): void {
    this.cache = this.emptyCache();
    this.saveCache();
  }

  /** Check if online */
  static isOnline(): boolean {
    return navigator.onLine;
  }
}
